import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { useLazyExportEmployeesToExcelQuery } from '@app/api/Users';
import {
  FRONT_WARNING,
  IMPORT_EMPLOYEES_PATH,
  TABLET,
  XLSX,
} from '@app/constants';
import { useUserPrint, useWindowSize } from '@app/hooks';
import {
  FileContext,
  HeaderContext,
  NotificationContext,
} from '@app/providers';
import { handleCatchError } from '@app/services';

export const useEmployeesPageHeader = () => {
  const { setFile, setExtension } = useContext(FileContext);
  const { innerWidth } = useWindowSize();
  const isMobile = innerWidth <= TABLET;
  const { header } = useContext(HeaderContext);
  const navigate = useNavigate();
  const { activeBranchId } = useUserPrint();
  const { showNotification } = useContext(NotificationContext);
  const [exportEmployeesToExcel] = useLazyExportEmployeesToExcelQuery();

  const exportToExcel = useCallback(
    async (searchId: number) => {
      if (searchId) {
        try {
          const parsedJson = await exportEmployeesToExcel({
            ActiveBranchId: activeBranchId,
            searchId,
          }).unwrap();

          const clientsListPage = document.querySelector('.bc-clients-list');
          const downloadButton = document.createElement('a');

          if (!parsedJson)
            return showNotification(
              'При поиске файла для скачивания что-то пошло не так',
              FRONT_WARNING
            );

          downloadButton.setAttribute(
            'href',
            `data:${XLSX};base64,${parsedJson?.Data}`
          );

          downloadButton.setAttribute('download', parsedJson.Name || '');
          downloadButton.setAttribute('style', 'display: hidden');
          clientsListPage?.appendChild(downloadButton);
          downloadButton.click();
        } catch (error) {
          showNotification(handleCatchError(error));
        }
      }
    },
    [activeBranchId, exportEmployeesToExcel, showNotification]
  );

  const updateEmployeesByExcel = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target?.files && event.target?.files[0];
      const filename = file?.name || 'Без названия';
      const extension = filename.split('.').pop();

      if (!extension)
        return showNotification('Неизвестное расширение файла', FRONT_WARNING);

      if (!file) return showNotification('Файл не найден', FRONT_WARNING);

      const reader = new FileReader();

      reader.readAsDataURL(file);
      reader.onload = () => {
        if (typeof reader.result !== 'string') return;

        const base64String = reader.result
          .replace('data:', '')
          .replace(/^.+,/, '');

        setFile(base64String);
        setExtension(extension);
        // Страница импорта читает файл из state навигации, а не из FileContext,
        // поэтому передаём его тем же способом, что и импорт клиентов.
        navigate(IMPORT_EMPLOYEES_PATH, {
          state: {
            file: base64String,
            extension,
          },
        });

        event.target.value = '';
      };
    },
    [navigate, setExtension, setFile, showNotification]
  );

  return {
    isMobile,
    header,
    exportToExcel,
    updateEmployeesByExcel,
  };
};
